/** Screen-space raycasting against the render and ghost sets. */

import {
  type Intersection,
  type Object3D,
  type PerspectiveCamera,
  Plane,
  Raycaster,
  Vector2,
  Vector3,
} from "three";
import type { Point } from "../model/types.ts";

export class Picker {
  private readonly raycaster = new Raycaster();
  private readonly plane = new Plane(new Vector3(0, 1, 0), 0);
  private readonly ndc = new Vector2();

  private readonly canvas: HTMLCanvasElement;
  private readonly camera: PerspectiveCamera;

  constructor(canvas: HTMLCanvasElement, camera: PerspectiveCamera) {
    this.canvas = canvas;
    this.camera = camera;
  }

  private aim(event: PointerEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    this.ndc.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(this.ndc, this.camera);
  }

  first(event: PointerEvent, targets: Object3D[]): Intersection | null {
    this.aim(event);
    return this.raycaster.intersectObjects(targets, false)[0] ?? null;
  }

  /** Where the pointer ray meets the horizontal plane at height `y`. */
  onPlane(event: PointerEvent, y: number): Point | null {
    this.aim(event);
    this.plane.constant = -y;

    const hit = new Vector3();
    if (!this.raycaster.ray.intersectPlane(this.plane, hit)) {
      return null;
    }

    return { x: hit.x, z: hit.z };
  }
}
